/**
 * GroceryMembersPanel — the Owner adds users and sets the three switches.
 * Every switch is enforced on the SERVER; this is only the control surface.
 * Pay requires Approve, and the Owner always has all three.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, UserPlus, Link2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { SheetLayer } from '../../lib/phoneSheet';

const API = (typeof window !== 'undefined' ? window.location.origin : '');

export default function GroceryMembersPanel({ session, onClose }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [draft, setDraft] = useState({ name: '', pin: '', phone: '', preset: 'add_only' });

    const call = useCallback(async (path, opts = {}) => {
        const r = await fetch(`${API}/api/grocery${path}`, {
            ...opts,
            headers: { 'Content-Type': 'application/json', 'X-Grocery-Session': session },
            body: opts.body ? JSON.stringify(opts.body) : undefined,
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.detail || 'error');
        return j;
    }, [session]);

    const load = useCallback(async () => {
        try {
            setData(await call('/members'));
            setFailed(false);
        } catch (_e) { setFailed(true); }
    }, [call]);
    useEffect(() => { load(); }, [load]);

    const addMember = async () => {
        if (!draft.name.trim()) { toast.error(t('grocery.members.needName')); return; }
        try {
            const m = await call('/members', { method: 'POST', body: draft });
            toast.success(t('grocery.members.added', { name: m.name }));
            setDraft({ name: '', pin: '', phone: '', preset: 'add_only' });
            load();
        } catch (e) {
            toast.error(String(e.message).startsWith('member_limit_reached')
                ? t('grocery.members.limit', { max: data?.max_members })
                : t('grocery.members.failed'));
        }
    };

    const toggle = async (m, key, value) => {
        try {
            await call(`/members/${m.id}`, { method: 'PATCH', body: { [key]: value } });
            load();
        } catch (_e) { toast.error(t('grocery.members.failed')); }
    };

    if (!data) {
        if (!failed) return null;
        return (
            <SheetLayer>
            <div className="yot-sheet z-50 flex items-center justify-center bg-slate-900/60 p-6 backdrop-blur-sm"
                data-grocery-sheet="true">
                <div className="flex flex-col items-center gap-3 rounded-2xl bg-white p-6 dark:bg-slate-900">
                    <p className="text-sm font-semibold text-foreground"
                        data-testid="grocery-members-load-failed">{t('grocery.couldNotLoad')}</p>
                    <div className="flex gap-2">
                        <Button onClick={load} className="min-h-[44px]"
                            data-testid="grocery-members-load-retry">{t('grocery.tryAgain')}</Button>
                        <Button variant="outline" onClick={onClose} className="min-h-[44px]"
                            data-testid="grocery-members-load-close">{t('grocery.close')}</Button>
                    </div>
                </div>
            </div>
            </SheetLayer>
        );
    }

    return (
        <SheetLayer>
        <div className="yot-sheet z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-grocery-sheet="true"
            data-testid="grocery-members-panel">
            <div className="yot-sheet-bottom w-full sm:max-w-lg bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-2xl px-5 pt-5 max-h-[92%] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('grocery.members.title')}
                    </h2>
                    <button onClick={onClose} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                        data-testid="grocery-members-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="space-y-3">
                    {data.members.map((m) => (
                        <div key={m.id} className="rounded-xl border border-slate-200 dark:border-slate-700/60 p-4"
                            data-testid={`grocery-member-${m.id}`}>
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <p className="font-semibold text-foreground">{m.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {m.role === 'owner' ? t('grocery.members.owner') : t('grocery.members.member')}
                                        {m.has_pin ? ` · ${t('grocery.members.hasPin')}` : ''}
                                    </p>
                                </div>
                                {m.role !== 'owner' && (
                                    <button onClick={async () => {
                                        try {
                                            await call(`/members/${m.id}`, { method: 'DELETE' });
                                        } catch (_e) { toast.error(t('grocery.members.failed')); }
                                        load();
                                    }}
                                        className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-rose-500"
                                        data-testid={`grocery-member-remove-${m.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            <div className="mt-3 space-y-2">
                                {[['can_add', 'addItems'], ['can_approve', 'approveOrders'], ['can_pay', 'pay']].map(([key, label]) => (
                                    <div key={key} className="flex items-center justify-between min-h-[44px]">
                                        <span className="text-sm text-foreground">{t(`grocery.members.${label}`)}</span>
                                        <Switch checked={!!m[key]} disabled={m.role === 'owner'}
                                            onCheckedChange={(v) => toggle(m, key, v)}
                                            data-testid={`grocery-switch-${key}-${m.id}`} />
                                    </div>
                                ))}
                                {!m.can_approve && m.can_pay === false && (
                                    <p className="text-xs text-muted-foreground">{t('grocery.members.payNeedsApprove')}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-5 pt-5 border-t border-slate-200 dark:border-slate-800 space-y-3">
                    <p className="text-sm font-semibold text-foreground">{t('grocery.members.addUser')}</p>
                    <div>
                        <Label className="mb-1.5 block">{t('grocery.members.name')}</Label>
                        <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            className="min-h-[44px]" data-testid="grocery-new-member-name" />
                    </div>
                    <div>
                        <Label className="mb-1.5 block">{t('grocery.members.pinOptional')}</Label>
                        <Input value={draft.pin} inputMode="numeric" maxLength={4}
                            onChange={(e) => setDraft({ ...draft, pin: e.target.value })}
                            className="min-h-[44px]" data-testid="grocery-new-member-pin" />
                    </div>
                    <div className="flex gap-2">
                        {['add_only', 'add_and_approve'].map((p) => (
                            <Button key={p} variant={draft.preset === p ? 'default' : 'outline'}
                                onClick={() => setDraft({ ...draft, preset: p })}
                                className="flex-1 min-h-[44px]" data-testid={`grocery-preset-${p}`}>
                                {t(`grocery.members.preset_${p}`)}
                            </Button>
                        ))}
                    </div>
                    <Button onClick={addMember} className="w-full min-h-[44px] gap-2" data-testid="grocery-add-member-btn">
                        <UserPlus className="w-4 h-4" />{t('grocery.members.addUser')}
                    </Button>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Link2 className="w-3.5 h-3.5" />{t('grocery.members.inviteNote')}
                    </p>
                </div>
            </div>
        </div>
        </SheetLayer>
    );
}
